import { useCallback, useEffect, useState } from "react"
import { useAppSelector } from "@/store"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { hasPermission } from "@/store/permissionsSlice"
import {
  useGetBookingFormDataQuery,
  useGetBookingQuery,
  useGetBookingRequestStatusQuery,
  useGetBookingStatusHistoryQuery,
} from "../bookingCalendarApi"
import type { BookingDetails } from "../bookingCalendarApi"
import { isPlaceholderEmail } from "../lib/attendeeContact"
import { longDateLabel, minutesToLabel, zonedDateAndMinutes } from "../lib/siteTime"
import { AppointmentDialogFooter, AppointmentFooterSlot } from "./AppointmentDialogFooter"
import { BookingDetailsPanel, BookingStatusHistory } from "./BookingDetailsPanel"
import { useGetHostRescheduleOptionsQuery } from "../api/rescheduleApi"
import { bookingActions, findBookingAction } from "./actions/registry"
import { mayOfferReschedule } from "./actions/RescheduleAction"
import { useOutcomeGateRefresh } from "./actions/StatusAction"
import type { BookingActionContext, DialogMode } from "./actions/types"

/**
 * One appointment, in full.
 *
 * Cal is the system of record for the booking itself; CMS is the record of what happened at it. So
 * there are two kinds of write here. Somebody holding `appointments:manage` can cancel or move the
 * appointment before it starts, both of which go through Cal. And the appointment's host — or an
 * administrator — can record its outcome (Complete, No show, …) once it has started; which of those a
 * given person may pick is the server's call, sent with the booking's status (see
 * `BookingStatusView.allowedStatuses`).
 *
 * A rescheduled booking links to the one that replaced it, and following the link swaps this dialog
 * over to that booking rather than stacking a second one on top.
 */
export function AppointmentDialog({ booking, staffName, timeZone, onClose }: {
  booking: BookingDetails | undefined
  /** The team member hosting it, as the grid labels them. */
  staffName: string | undefined
  timeZone: string
  onClose: () => void
}) {
  // A booking reached by following a reschedule link. Cleared whenever the caller opens a different
  // appointment, so the dialog always starts on the one that was clicked.
  const [followedUid, setFollowedUid] = useState<string | null>(null)
  useEffect(() => { setFollowedUid(null) }, [booking?.uid])

  const followed = useGetBookingQuery(followedUid ?? "", { skip: !followedUid })
  const shown = followedUid ? followed.data : booking

  if (!booking) return null

  if (followedUid && !shown) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Appointment</DialogTitle>
            <DialogDescription>
              {followed.isError ? "That appointment is no longer in the scheduling system." : "Loading…"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowedUid(null)}>Back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <AppointmentDialogBody
      booking={shown ?? booking}
      // The grid's label is about the booking it drew; one reached by a link is labelled from CMS.
      staffName={followedUid ? undefined : staffName}
      timeZone={timeZone}
      onFollow={setFollowedUid}
      onClose={onClose}
    />
  )
}

function AppointmentDialogBody({ booking, staffName, timeZone, onFollow, onClose }: {
  booking: BookingDetails
  staffName: string | undefined
  timeZone: string
  onFollow: (bookingUid: string) => void
  onClose: () => void
}) {
  const { permissions } = useAppSelector(state => state.permissions)
  const canManage = hasPermission(permissions, "appointments:manage")
  const canViewPeople = hasPermission(permissions, "people:view")

  /** Which write is armed, if any. Each one's own state lives in its action; see `actions/registry`. */
  const [mode, setMode] = useState<DialogMode>("reading")
  const [busy, setBusy] = useState(false)

  const uid = booking.uid

  // A dialog opened on another appointment must not carry the last one's half-typed note — or,
  // worse, its armed confirmation — over to it.
  useEffect(() => {
    setMode("reading")
  }, [uid])

  const disarm = useCallback(() => setMode("reading"), [])

  // What the applicant filled in, from CMS. Skipped entirely until there is a booking to ask about,
  // and a 404 is an ordinary answer rather than a failure: an appointment made directly in Cal, or
  // one predating the booking-request table, has no form behind it and the section is simply absent.
  //
  // Refetched on every open rather than cached (see the endpoint), because the response carries a
  // Social Security number and a recorded disclosure.
  const { data: formData } = useGetBookingFormDataQuery(uid, { skip: !uid })

  // Where the booking stands in CMS, and what the signed-in person may do about it. Keyed on the
  // request id the form data carries, so a booking with no CMS row has no status section at all.
  const requestId = formData?.requestId
  const {
    data: statusView,
    fulfilledTimeStamp: statusFetchedAt,
    isFetching: statusFetching,
    refetch: refetchStatus,
  } = useGetBookingRequestStatusQuery(requestId ?? "", { skip: !requestId })
  const { data: history, isError: historyFailed } = useGetBookingStatusHistoryQuery(requestId ?? "", { skip: !requestId })

  const start = zonedDateAndMinutes(booking.start, timeZone)
  const end = booking.end ? zonedDateAndMinutes(booking.end, timeZone) : null
  const attendee = booking.attendees[0]

  // Held apart from the attendee because it has three states rather than two: an address, nothing at
  // all, and the address CMS invents for a booking made without one — see `isPlaceholderEmail`.
  const attendeeEmail = attendee?.email?.trim()
  const emailIsPlaceholder = isPlaceholderEmail(attendeeEmail)

  // Cal's start rather than CMS's, so a booking CMS has no row for is judged by the same clock.
  const hasStarted = new Date(booking.start).getTime() <= Date.now()

  const baseCtx: BookingActionContext = {
    booking,
    uid,
    timeZone,
    canManage,
    hasStarted,
    formData,
    requestId,
    statusView,
    statusFetchedAt,
    attendeeName: attendee?.name,
    emailIsPlaceholder,
  }

  // Asks the server (which reads Cal) only for a booking the local precheck says could be moved today,
  // so opening any other appointment costs nothing. The server's answer decides whether Reschedule shows.
  const { data: rescheduleOptions } = useGetHostRescheduleOptionsQuery(requestId ?? "", {
    skip: !requestId || !mayOfferReschedule(baseCtx),
  })
  const ctx: BookingActionContext = { ...baseCtx, rescheduleOptions }

  // The server offers no outcome before the start, so the list on hand then goes stale at the start
  // (F7): fetch it again at that moment rather than letting "Update status" vanish.
  useOutcomeGateRefresh(ctx, refetchStatus, statusFetching)

  // Where the footers draw; see `AppointmentDialogFooter`. State rather than a ref, so the footers
  // render again into it once it has mounted.
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null)

  const armed = mode === "reading" ? undefined : findBookingAction(mode)
  const ArmedForm = armed?.Form
  const when = [
    longDateLabel(start.date),
    minutesToLabel(start.minutes) + (end ? ` – ${minutesToLabel(end.minutes)}` : ""),
  ].join(" · ")

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose() }}>
      {/* A scrolling body over a pinned footer region. The footer draws into that region (see
          `AppointmentDialogFooter`), so the buttons stay in view however long the booking runs. */}
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <AppointmentFooterSlot.Provider value={footerSlot}>
          <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto overflow-x-hidden p-6">
            <DialogHeader>
              <DialogTitle>{attendee?.name ?? "Appointment"}</DialogTitle>
              {/* The day as well as the time: opened from the incomplete list, or by following a
                  reschedule, there is no grid column beside the dialog to say which day this is. */}
              <DialogDescription>
                {when}
                {booking.siteName && ` · ${booking.siteName}`}
              </DialogDescription>
            </DialogHeader>

            <BookingDetailsPanel
              booking={booking}
              staffName={staffName}
              formData={formData}
              statusView={statusView}
              attendeeEmail={attendeeEmail}
              emailIsPlaceholder={emailIsPlaceholder}
              onFollow={onFollow}
              canViewPeople={canViewPeople}
            />

            <AppointmentDialogFooter>
              {/* Its own row under the buttons, the full width of the footer. First in a column-reverse
                  stack on narrow screens is the bottom, so the order flips there. */}
              {requestId && (
                <BookingStatusHistory
                  history={history}
                  isError={historyFailed}
                  className="order-first sm:order-last sm:basis-full"
                />
              )}
              <TooltipProvider>
                {bookingActions.filter(action => action.isAvailable(ctx)).map(action => {
                  const disabledReason = action.disabledReason?.(ctx)
                  if (disabledReason === undefined) {
                    return (
                      <Button
                        key={action.id}
                        variant={action.triggerVariant}
                        className={action.triggerClassName}
                        onClick={() => setMode(action.id)}
                      >
                        {action.label}
                      </Button>
                    )
                  }
                  // A disabled button fires no pointer events, so the tooltip hangs off a focusable wrapper.
                  return (
                    <Tooltip key={action.id}>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} aria-label={`${action.label}: ${disabledReason}`}>
                          <Button
                            variant={action.triggerVariant}
                            className={cn(action.triggerClassName, "pointer-events-none")}
                            disabled
                          >
                            {action.label}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{disabledReason}</TooltipContent>
                    </Tooltip>
                  )
                })}
              </TooltipProvider>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </AppointmentDialogFooter>
          </div>
        </AppointmentFooterSlot.Provider>

        <div ref={setFooterSlot} className="border-t px-6 py-4 empty:hidden" />

        {/* An armed write, in a dialog of its own stacked over this one. Dismissing it goes back to the
            booking, not out of it, and not while its write is in flight. Keyed on the booking so a
            dialog switched to another appointment never shows the last one's half-filled form. */}
        {armed && ArmedForm && (
          <Dialog open onOpenChange={(open) => { if (!open && !busy) disarm() }}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{armed.label}</DialogTitle>
                <DialogDescription>
                  {attendee?.name ? `${attendee.name} · ${when}` : when}
                </DialogDescription>
              </DialogHeader>
              {/* No footer region in here: the form's footer draws in place, at the dialog's foot. */}
              <AppointmentFooterSlot.Provider value={null}>
                <ArmedForm key={uid} ctx={ctx} onDone={disarm} onClose={onClose} onBusyChange={setBusy} />
              </AppointmentFooterSlot.Provider>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  )
}
